/**
 * DimensionColumn - Single dimension column with co-occurring causes
 */

import { Badge } from '../../../../../ui/badge';
import { Progress } from '../../../../../ui/progress';
import { ConductScoreCircle } from '../../../../../ui/conduct-score-circle';
import { Check } from 'lucide-react';
import { cn } from '../../../../../../lib/utils';
import type { DimensionColumnProps } from '../../types';
import { CONDUCT_DIMENSIONS, DIMENSION_ICONS } from '../../constants';

export function DimensionColumn({
  data,
  onClick,
  onCauseClick,
  isActive,
  className,
}: DimensionColumnProps) {
  const meta = CONDUCT_DIMENSIONS[data.dimension];
  const Icon = DIMENSION_ICONS[data.dimension];

  return (
    <div
      className={cn(
        'space-y-4 rounded-[22px] border border-border/70 bg-background/90 p-4 shadow-[var(--shadow-card)] backdrop-blur-sm transition-colors cursor-pointer hover:border-primary/20 hover:bg-accent/40',
        isActive && 'border-primary/25 bg-[var(--go-good-vibes-tint)]',
        className
      )}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-pressed={isActive}
      aria-label={`${data.displayName} dimension filter`}
      data-testid={`dimension-column-${data.dimension}`}
    >
      {/* Header - clickable to toggle filter */}
      <div className="flex items-center gap-3">
        <div
          className="rounded-2xl border border-border/60 p-2"
          style={{ backgroundColor: `${meta.color}20` }}
        >
          <span style={{ color: meta.color }}>
            <Icon className="h-5 w-5" />
          </span>
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground">{data.displayName}</h3>
          <p className="text-xs text-muted-foreground">{data.description}</p>
        </div>
        {isActive ? (
          <div className="rounded-full bg-primary p-1">
            <Check className="h-4 w-4 text-primary-foreground" />
          </div>
        ) : (
          <div className="rounded-full border border-muted-foreground/30 p-1">
            <div className="h-4 w-4" />
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-3">
        <ConductScoreCircle
          score={data.avgConductScore}
          size="sm"
          showDecimal
        />
        <div className="flex items-baseline gap-1.5">
          <p className="text-xl font-semibold tabular-nums">{data.recordCount.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground">records</p>
        </div>
      </div>

      {/* Top Causes with strength bars */}
      {data.topCauses.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
            Top Co-occurring Causes
          </p>
          <div className="space-y-2.5">
            {data.topCauses.map((cause) => (
              <div
                key={cause.labelId}
                className="cursor-pointer rounded-2xl px-3 py-2 transition-colors hover:bg-accent/50"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onCauseClick(data.dimension, cause.labelId, cause.labelName);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onCauseClick(data.dimension, cause.labelId, cause.labelName);
                  }
                }}
              >
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="truncate font-medium">{cause.labelName}</span>
                  <div className="flex flex-shrink-0 items-center gap-2 text-xs text-muted-foreground">
                    <span className="tabular-nums">{cause.percentage.toFixed(0)}%</span>
                    <Badge variant="outline" className="px-1.5 text-xs">
                      {cause.avgSeverity.toFixed(1)}
                    </Badge>
                  </div>
                </div>
                <Progress
                  value={cause.percentage}
                  max={100}
                  className="h-2"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {data.topCauses.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No co-occurring causes found
        </p>
      )}
    </div>
  );
}
